/**
 * Audit Logs Page
 */

import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import type { AuditLog } from '../api/types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { Input, Select } from '../components/Input';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  EmptyState,
  Badge,
} from '../components/Table';
import { Modal } from '../components/Modal';
import { RefreshCw, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, subDays } from 'date-fns';

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'created', label: 'Created' },
  { value: 'used', label: 'Used' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'expired', label: 'Expired' },
  { value: 'failed_auth', label: 'Failed Auth' },
  { value: 'rate_limited', label: 'Rate Limited' },
];

const ACTION_COLORS: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'gray'> = {
  created: 'success',
  used: 'info',
  revoked: 'warning',
  expired: 'gray',
  failed_auth: 'danger',
  rate_limited: 'warning',
};

export function AuditLogsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 50;

  const [filters, setFilters] = useState({
    action: '',
    keyUuid: '',
    from: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    loadLogs();
  }, [filters, page]);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.listAuditLogs({
        action: filters.action || undefined,
        keyUuid: filters.keyUuid || undefined,
        from: filters.from,
        to: filters.to,
        limit: pageSize + 1, // Get one extra to check if there are more
        offset: page * pageSize,
      });

      // Check if there are more results
      if (res.logs.length > pageSize) {
        setHasMore(true);
        setLogs(res.logs.slice(0, pageSize));
      } else {
        setHasMore(false);
        setLogs(res.logs);
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPage(0); // Reset to first page when filters change
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-500 mt-1">
            View authentication and usage history
          </p>
        </div>
        <Button variant="secondary" onClick={loadLogs}>
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <Select
                label="Action"
                options={ACTION_OPTIONS}
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              />
            </div>
            <div className="w-64">
              <Input
                label="Key UUID"
                placeholder="Filter by key UUID..."
                value={filters.keyUuid}
                onChange={(e) => handleFilterChange('keyUuid', e.target.value)}
              />
            </div>
            <div className="w-40">
              <Input
                label="From"
                type="date"
                value={filters.from}
                onChange={(e) => handleFilterChange('from', e.target.value)}
              />
            </div>
            <div className="w-40">
              <Input
                label="To"
                type="date"
                value={filters.to}
                onChange={(e) => handleFilterChange('to', e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              onClick={() => {
                setFilters({
                  action: '',
                  keyUuid: '',
                  from: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
                  to: format(new Date(), 'yyyy-MM-dd'),
                });
                setPage(0);
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Logs {page > 0 && `(Page ${page + 1})`}
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Timestamp</TableHeaderCell>
                <TableHeaderCell>Action</TableHeaderCell>
                <TableHeaderCell>Key Prefix</TableHeaderCell>
                <TableHeaderCell>IP Address</TableHeaderCell>
                <TableHeaderCell>Endpoint</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell align="right">Details</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    Loading...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <EmptyState message="No audit logs found" colSpan={7} />
              ) : (
                logs.map((log) => (
                  <TableRow key={log.uuid}>
                    <TableCell>
                      <span className="whitespace-nowrap">
                        {format(new Date(log.timestamp), 'MMM d, yyyy')}
                      </span>
                      <span className="text-gray-500 ml-2">
                        {format(new Date(log.timestamp), 'HH:mm:ss')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ACTION_COLORS[log.action] || 'gray'}>
                        {formatAction(log.action)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                        {log.keyPrefix}...
                      </code>
                    </TableCell>
                    <TableCell>{log.ip || '-'}</TableCell>
                    <TableCell>
                      {log.endpoint ? (
                        <span className="flex items-center gap-2">
                          {log.method && (
                            <Badge variant="info">{log.method}</Badge>
                          )}
                          <span className="text-sm truncate max-w-48" title={log.endpoint}>
                            {log.endpoint}
                          </span>
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {log.responseStatus ? (
                        <Badge
                          variant={
                            log.responseStatus >= 200 && log.responseStatus < 300
                              ? 'success'
                              : log.responseStatus >= 400
                              ? 'danger'
                              : 'warning'
                          }
                        >
                          {log.responseStatus}
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {log.details && Object.keys(log.details).length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {page * pageSize + 1} - {page * pageSize + logs.length} results
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={!hasMore}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Log Details Modal */}
      {selectedLog && (
        <Modal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Log Details"
          size="md"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Timestamp</label>
                <p className="font-medium">
                  {format(new Date(selectedLog.timestamp), 'MMM d, yyyy HH:mm:ss')}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Action</label>
                <p>
                  <Badge variant={ACTION_COLORS[selectedLog.action] || 'gray'}>
                    {formatAction(selectedLog.action)}
                  </Badge>
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Key Prefix</label>
                <p className="font-mono">{selectedLog.keyPrefix}...</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Key UUID</label>
                <p className="font-mono text-sm break-all">{selectedLog.keyUuid}</p>
              </div>
              {selectedLog.ip && (
                <div>
                  <label className="text-sm text-gray-500">IP Address</label>
                  <p className="font-medium">{selectedLog.ip}</p>
                </div>
              )}
              {selectedLog.userAgent && (
                <div className="col-span-2">
                  <label className="text-sm text-gray-500">User Agent</label>
                  <p className="text-sm break-all">{selectedLog.userAgent}</p>
                </div>
              )}
            </div>
            {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
              <div>
                <label className="text-sm text-gray-500">Details</label>
                <pre className="mt-1 p-3 bg-gray-100 rounded-lg text-sm overflow-auto max-h-64">
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
